import {useMemo} from 'react';

import {RampIdType} from '../../../generated/graphql';
import type * as QueryTypes from '../../../util/queryTypes';
import type * as RunFeed from '../../../util/runfeed';
import * as Run from '../../../util/runs';
import type * as RunTypes from '../../../util/runTypes';
import {useEntityRampFlag} from '../../../util/useRampFlag';

const RUN_DISPLAY_NAME_COL = 'run:displayName';

// Run attributes (as server paths) the backend's SQL fast path supports for
// both grouping and the capped run-scan order. Must stay in sync with
// flatRunGroupSummaryRunKeys in services/gorilla/mysql/flat_runs_metadata.go
// and runSelectorGroupSummaryOrderKeys in
// services/gorilla/metadata/experiments/run_selector_group_order.go.
const LEAN_GROUPED_SHARED_RUN_KEYS = [
  'displayName',
  'group',
  'host',
  'jobType',
  'name',
  'state',
  'sweep',
  'sweepName',
];

// Grouping additionally supports username; anything else falls back to
// in-memory grouping server-side.
const LEAN_GROUPED_RUN_ATTRIBUTES = new Set([
  ...LEAN_GROUPED_SHARED_RUN_KEYS,
  'username',
]);

// Native run-metadata columns the capped scan can order by, plus timestamps
// and duration. Group order is a subset: timestamps, duration, or the current
// grouping key.
const LEAN_GROUPED_ORDER_KEYS = new Set([
  ...LEAN_GROUPED_SHARED_RUN_KEYS,
  'createdAt',
  'updatedAt',
  'heartbeatAt',
  'duration',
]);

// Sort keys come in both spellings depending on their origin (table columns
// use camelCase, saved views/parsed specs use snake_case); the backend order
// parser accepts both, so normalize to camelCase before checking.
function toCanonicalOrderKey(serverPath: string): string {
  const camelCasePath = serverPath.replace(/_([a-z])/g, (_, c: string) =>
    c.toUpperCase()
  );
  switch (camelCasePath) {
    case 'groupName':
      return 'group';
    case 'subgroupName':
      return 'jobType';
    case 'sweep':
      return 'sweepName';
    default:
      return camelCasePath;
  }
}

// Every sort must be a native run-metadata column, a timestamp/duration, or
// the top-level grouping key. Config/summary sorts the fast path cannot honor
// take the legacy query.
export function supportsLeanGroupedRunsQueryOrder(
  sort: QueryTypes.Sort,
  grouping: RunTypes.Key[]
) {
  // An empty sort means the backend's default (newest first) order.
  if (sort.keys.length === 0) {
    return true;
  }
  const topLevelGroupKey = grouping[0];
  if (topLevelGroupKey == null) {
    return false;
  }
  const topLevelGroupPath = Run.keyToServerPath(topLevelGroupKey);
  return sort.keys.every(({key}) => {
    const serverPath = Run.keyToServerPath(key);
    if (
      key.section === 'run' &&
      LEAN_GROUPED_ORDER_KEYS.has(toCanonicalOrderKey(serverPath))
    ) {
      return true;
    }
    if (key.section === 'run' && topLevelGroupKey.section === 'run') {
      return (
        toCanonicalOrderKey(serverPath) ===
        toCanonicalOrderKey(topLevelGroupPath)
      );
    }
    return serverPath === topLevelGroupPath;
  });
}

export function supportsLeanGroupedRunsQuery(grouping: RunTypes.Key[]) {
  if (grouping.length === 0) {
    return false;
  }
  // Compare server paths (not display names) since that is what the backend
  // receives as groupKeys.
  return grouping.every(
    key =>
      key.section === 'config' ||
      (key.section === 'run' &&
        LEAN_GROUPED_RUN_ATTRIBUTES.has(Run.keyToServerPath(key)))
  );
}

export function shouldUseLeanGroupedRunsQuery({
  grouping,
  hasPinnedColumns,
  isRampEnabled,
  isTableCollapsed,
  sort,
}: {
  grouping: RunTypes.Key[];
  hasPinnedColumns: boolean;
  /** Statsig entity gate `run_selector_sql_grouped_runs_entity` */
  isRampEnabled: boolean;
  isTableCollapsed: boolean;
  sort: QueryTypes.Sort;
}) {
  return (
    isRampEnabled &&
    grouping.length > 0 &&
    isTableCollapsed &&
    !hasPinnedColumns &&
    supportsLeanGroupedRunsQuery(grouping) &&
    supportsLeanGroupedRunsQueryOrder(sort, grouping)
  );
}

/**
 * True when a column other than the run name is pinned. Pinned columns need
 * full run metadata, which the lean grouped query does not fetch.
 */
export function hasPinnedColumnsForLeanQuery(
  columnPinned: RunFeed.Config['columnPinned']
): boolean {
  return Object.entries(columnPinned).some(
    ([accessor, pinned]) => pinned && accessor !== RUN_DISPLAY_NAME_COL
  );
}

/**
 * Whether the entity is ramped into the lean grouped-runs query. Same entity
 * gate the backend checks before using SQL group aggregation
 * (run.go canUseSQLGroupSummary), so one flag controls the whole lean path.
 */
export function useLeanGroupedRunsQueryRamp(entityName: string): boolean {
  return useEntityRampFlag(
    entityName,
    'run_selector_sql_grouped_runs_entity',
    RampIdType.EntityName
  );
}

/**
 * Decides whether the run selector should fetch grouped rows via the lean
 * `runSelectorGroups` query instead of the full runs query.
 */
export function useLeanGroupedRunsQuery({
  entityName,
  grouping,
  hasPinnedColumns,
  isTableCollapsed,
  sort,
}: {
  entityName: string;
  grouping: RunTypes.Key[];
  hasPinnedColumns: boolean;
  isTableCollapsed: boolean;
  sort: QueryTypes.Sort;
}): boolean {
  const isRampEnabled = useLeanGroupedRunsQueryRamp(entityName);
  return useMemo(
    () =>
      shouldUseLeanGroupedRunsQuery({
        grouping,
        hasPinnedColumns,
        isRampEnabled,
        isTableCollapsed,
        sort,
      }),
    [grouping, hasPinnedColumns, isRampEnabled, isTableCollapsed, sort]
  );
}
